import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Badge, Spinner } from 'react-bootstrap';
import { FaDownload, FaCalendarAlt, FaUsers, FaChartBar } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';
import '../styles/DoctorAttendanceSummery.css';

const EmployeeAttendanceSummary = () => {
     const [employeeData, setEmployeeData] = useState([]);
     const [summary, setSummary] = useState({});
     const [loading, setLoading] = useState(false);
     const getPreviousMonth = () => {
          const date = new Date();
          date.setMonth(date.getMonth() - 1);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          return `${year}-${month}`;
     };
     const [selectedMonth, setSelectedMonth] = useState(getPreviousMonth());
     const [selectedToMonth, setSelectedToMonth] = useState(getPreviousMonth());
     const [currentPage, setCurrentPage] = useState(1);
     const [searchTerm, setSearchTerm] = useState('');
     const recordsPerPage = 50;

     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const token = localStorage.getItem('token');

     // ✅ NEW: Handle from-month change and validate to-month
     const handleFromMonthChange = (e) => {
          const newFromMonth = e.target.value;
          setSelectedMonth(newFromMonth);

          // If to-month is less than from-month, set to-month equal to from-month
          if (selectedToMonth < newFromMonth) {
               setSelectedToMonth(newFromMonth);
          }
     };

     useEffect(() => {
          if (selectedMonth && selectedToMonth) {
               fetchEmployeeAttendance();
          }
     }, [selectedMonth, selectedToMonth]);


     const fetchEmployeeAttendance = async () => {
          setLoading(true);
          try {
               const response = await fetch(`${BASE_URL}/all-contractual-employee-attendance`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                         selectedMonth,
                         selectedToMonth,
                         emp_type: 'CONTRACTUAL_EMPLOYEE'
                    })
               });

               if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
               }

               const result = await response.json();
               console.log('Employee Attendance Response:', result);

               if (result.status && result.data) {
                    setEmployeeData(result.data);
                    setSummary(result.summary || {});
               } else {
                    setEmployeeData([]);
                    setSummary({});
               }
          } catch (error) {
               console.error('Error fetching employee attendance:', error);
               setEmployeeData([]);
               setSummary({});
          } finally {
               setLoading(false);
          }
     };

     // ✅ UPDATED: Initialize Employee Strategy for calculations
     const getEmployeeStrategy = () => {
          try {
               return AttendanceStrategyFactory.getStrategy('CONTRACTUAL_EMPLOYEE');
          } catch (error) {
               console.error('Error getting employee strategy:', error);
               return null;
          }
     };

     // ✅ UPDATED: Calculate employee stats using strategy
     const calculateEmployeeStats = (employee) => {
          const attendance = employee.attendance || {};
          const days = attendance.days || [];
          const dutyRoster = attendance.duty_roster || [];
          const holidays = attendance.holidays || [];
          const weekOff = attendance.employee_info?.week_off || 'Sunday';
          const strategy = getEmployeeStrategy();

          // Get current date for comparison
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Check if selected month is current month
          const isCurrentMonth = (() => {
               const now = new Date();
               const [fromYear, fromMonth] = selectedMonth.split('-').map(Number);
               return now.getFullYear() === fromYear && (now.getMonth() + 1) === fromMonth;
          })();

          // Get all dates in the selected month
          const [year, month] = selectedMonth.split('-').map(Number);
          const daysInMonth = new Date(year, month, 0).getDate();
          const allDates = [];
          for (let d = 1; d <= daysInMonth; d++) {
               const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
               allDates.push(dateStr);
          }

          // Prepare lookup for present, weekoff, holiday
          const presentDates = new Set(days.filter(day => day.punch_in && day.punch_out).map(day => day.date));
          const customWeekOffDates = dutyRoster
               .filter(r => {
                    if (!r.custom_weekoff_date || r.custom_weekoff_date === null || r.custom_weekoff_date === '') {
                         return false;
                    }
                    const weekOffDate = new Date(r.attendance_date);
                    weekOffDate.setHours(0, 0, 0, 0);
                    return !isCurrentMonth || weekOffDate <= today;
               })
               .map(r => r.attendance_date);
          const weekOffDates = new Set(customWeekOffDates);
          const holidayDates = new Set(
               holidays
                    .filter(h => {
                         const holidayDate = new Date(h.holiday_date);
                         holidayDate.setHours(0, 0, 0, 0);
                         return !isCurrentMonth || holidayDate <= today;
                    })
                    .map(h => h.holiday_date)
          );

          let presentDays = 0;
          let weekOffDays = 0;
          let holidayCount = 0;
          let absentDays = 0;
          let totalActualWorkingSeconds = 0;
          let totalConsideredWorkingSeconds = 0;

          // Helper function to format time HH:MM:SS to HH:MM
          const formatTime = (time) => {
               if (!time) return '';
               const timeParts = time.split(':');
               if (timeParts.length >= 2) {
                    return `${timeParts[0]}:${timeParts[1]}`;
               }
               return time;
          };

          // Extract shift timing from duty roster OR shift_description
          let defaultShiftTiming = 'N/A';
          if (dutyRoster.length > 0) {
               const shiftTimings = [...new Set(dutyRoster.map(r => {
                    if (r.in_time && r.out_time) {
                         return `${formatTime(r.in_time)} - ${formatTime(r.out_time)}`;
                    }
                    return null;
               }).filter(Boolean))];
               defaultShiftTiming = shiftTimings.length > 0 ? shiftTimings[0] : 'N/A';
          } else if (employee.shift_description) {
               const match = employee.shift_description.match(/(\d{2}:\d{2})\s+TO\s+(\d{2}:\d{2})/i);
               if (match) {
                    defaultShiftTiming = `${match[1]} - ${match[2]}`;
               } else {
                    defaultShiftTiming = employee.shift_description;
               }
          }

          // Loop through all dates in the month
          allDates.forEach(dateStr => {
               const dateObj = new Date(dateStr);
               dateObj.setHours(0, 0, 0, 0);
               if (isCurrentMonth && dateObj > today) return; // skip future dates

               if (presentDates.has(dateStr)) {
                    presentDays++;
                    // Add working hours
                    const day = days.find(d => d.date === dateStr);
                    if (day && day.punch_in && day.punch_out) {
                         const punchIn = new Date(day.punch_in);
                         const punchOut = new Date(day.punch_out);
                         const actualSeconds = Math.floor((punchOut - punchIn) / 1000);
                         totalActualWorkingSeconds += actualSeconds;

                         if (strategy) {
                              const employeeDataForStrategy = {
                                   weekAttendance: attendance.weekAttendance || [],
                                   allDays: days,
                                   region: employee.region || attendance.employee_region || 'Other',
                                   emp_id: employee.emp_id
                              };
                              try {
                                   const roster = dutyRoster.find(r => r.attendance_date === dateStr);
                                   const result = strategy.calculateAttendanceStatus(day, roster, holidays, employeeDataForStrategy);
                                   if (result.workingHours && result.workingHours !== '-') {
                                        const [hours, minutes, seconds] = result.workingHours.split(':').map(Number);
                                        const consideredSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);
                                        totalConsideredWorkingSeconds += consideredSeconds;
                                   } else {
                                        totalConsideredWorkingSeconds += actualSeconds;
                                   }
                              } catch (error) {
                                   totalConsideredWorkingSeconds += actualSeconds;
                              }
                         } else {
                              totalConsideredWorkingSeconds += actualSeconds;
                         }
                    }
               } else if (weekOffDates.has(dateStr)) {
                    weekOffDays++;
               } else if (holidayDates.has(dateStr)) {
                    holidayCount++;
               } else {
                    absentDays++;
               }
          });

          const effectiveTotalDays = presentDays + weekOffDays + holidayCount + absentDays;
          if (absentDays < 0) absentDays = 0;

          const attendancePercentage = effectiveTotalDays > 0 ? ((presentDays / effectiveTotalDays) * 100).toFixed(1) : '0.0';

          return {
               totalDays: effectiveTotalDays,
               presentDays,
               weekOffDays,
               absentDays,
               attendancePercentage,
               totalActualWorkingHours: (totalActualWorkingSeconds / 3600).toFixed(1),
               totalConsideredWorkingHours: (totalConsideredWorkingSeconds / 3600).toFixed(1),
               defaultShiftTiming
          };
     };

     const filteredEmployees = employeeData.filter(employee =>
          employee.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.employee_code.toLowerCase().includes(searchTerm.toLowerCase())
     );

     const paginatedEmployees = filteredEmployees.slice(
          (currentPage - 1) * recordsPerPage,
          currentPage * recordsPerPage
     );

     const totalPages = Math.ceil(filteredEmployees.length / recordsPerPage);

     // ✅ UPDATED: Enhanced exportToExcel with strategy integration
     const exportToExcel = () => {
          if (employeeData.length === 0) {
               console.warn('No data to export');
               return;
          }

          const strategy = getEmployeeStrategy();
          console.log('📊 Excel Export - Using strategy:', strategy?.constructor?.name || 'None');

          // Create summary sheet data
          const summaryData = [{
               'Report Type': 'Employee Attendance Summary',
               'Period': `${selectedMonth} to ${selectedToMonth}`,
               'Total Employees': summary.total_employees || 0,
               'Employees with Attendance': summary.employees_with_attendance || 0,
               'Total Attendance Records': summary.total_attendance_records || 0,
               'Generated On': new Date().toLocaleString('en-IN'),
               'Strategy Used': strategy?.constructor?.name || 'Default'
          }];

          // Create detailed summary data
          const detailedData = employeeData.map(employee => {
               const stats = calculateEmployeeStats(employee);

               return {
                    'Employee Code': employee.employee_code,
                    'Employee Name': employee.employee_name,
                    'Employee Type': employee.emp_type,
                    'Default Shift Timing': stats.defaultShiftTiming,
                    'Present Days': stats.presentDays,
                    'Week Off Days': stats.weekOffDays,
                    'Absent Days': stats.absentDays,
                    'Attendance Percentage': stats.attendancePercentage,
                    'Total Actual Working Hours': stats.totalActualWorkingHours,
                    'Total Considered Working Hours': stats.totalConsideredWorkingHours,
               };
          });

          // ✅ UPDATED: Create comprehensive daily attendance with proper strategy integration
          const allEmployeesDailyData = [];

          employeeData.forEach((employee) => {
               const attendance = employee.attendance || {};
               const days = attendance.days || [];
               const dutyRoster = attendance.duty_roster || [];
               const holidays = attendance.holidays || [];
               const weekOff = attendance.employee_info?.week_off || 'Sunday';

               if (days.length > 0) {
                    days.forEach(day => {
                         const date = new Date(day.date);
                         const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });

                         // Find matching duty roster entry for this date
                         const rosterEntry = dutyRoster.find(roster => roster.attendance_date === day.date);

                         // Check if this day is a week off
                         const isWeekOff = rosterEntry?.custom_weekoff_date || dayName === weekOff;

                         // Extract shift hours from duty roster
                         let shiftHours = 0;
                         if (rosterEntry && rosterEntry.total_hours) {
                              const [hours, minutes] = rosterEntry.total_hours.split(':').map(Number);
                              shiftHours = hours + (minutes / 60);
                         } else if (day.shift_total_minutes) {
                              shiftHours = day.shift_total_minutes / 60;
                         }

                         // Calculate actual worked hours
                         let actualWorkedHours = '0.00';
                         let actualWorkedSeconds = 0;
                         if (day.punch_in && day.punch_out) {
                              const punchIn = new Date(day.punch_in);
                              const punchOut = new Date(day.punch_out);
                              actualWorkedSeconds = Math.floor((punchOut - punchIn) / 1000);
                              actualWorkedHours = (actualWorkedSeconds / 3600).toFixed(2);
                         }

                         // Calculate considered worked hours using strategy
                         let consideredWorkedHours = '0.00';
                         let strategyStatus = 'Unknown';
                         let strategyRemarks = '';

                         if (strategy) {
                              const employeeDataForStrategy = {
                                   weekAttendance: attendance.weekAttendance || [],
                                   allDays: days,
                                   region: employee.region || attendance.employee_region || 'Other',
                                   emp_id: employee.emp_id
                              };

                              const strategyResult = strategy.calculateAttendanceStatus(day, rosterEntry, holidays, employeeDataForStrategy);
                              strategyStatus = strategyResult.status || 'Unknown';
                              strategyRemarks = strategyResult.remarks || '';

                              if (strategyResult.workingHours && strategyResult.workingHours !== '-') {
                                   const [hours, minutes, seconds] = strategyResult.workingHours.split(':').map(Number);
                                   const totalSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);
                                   consideredWorkedHours = (totalSeconds / 3600).toFixed(2);
                              }
                         } else {
                              consideredWorkedHours = actualWorkedHours;

                              if (day.punch_in && day.punch_out) {
                                   strategyStatus = 'Present';
                              } else if (isWeekOff) {
                                   strategyStatus = 'Week Off';
                              } else {
                                   strategyStatus = 'Absent';
                              }
                         }

                         const formatShiftTime = (time) => {
                              if (!time || time === 'N/A') return 'N/A';
                              const timeParts = time.split(':');
                              if (timeParts.length >= 2) {
                                   return `${timeParts[0]}:${timeParts[1]}`;
                              }
                              return time;
                         };

                         allEmployeesDailyData.push({
                              'Employee Code': employee.employee_code,
                              'Employee Name': employee.employee_name,
                              'Date': date.toLocaleDateString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit' }),
                              'Day': dayName,
                              'Punch In': day.punch_in ? new Date(day.punch_in).toLocaleTimeString('en-IN', { hour12: false, hour: '2-digit', minute: '2-digit' }) : 'N/A',
                              'Punch Out': day.punch_out ? new Date(day.punch_out).toLocaleTimeString('en-IN', { hour12: false, hour: '2-digit', minute: '2-digit' }) : 'N/A',
                              'Actual Worked Hours': actualWorkedHours,
                              'Considered Worked Hours': consideredWorkedHours,
                              'Status': strategyStatus,
                              'Shift Name': rosterEntry?.shift_name || 'N/A',
                              'Shift Hours': shiftHours.toFixed(2),
                              'Shift In Time': formatShiftTime(rosterEntry?.in_time),
                              'Shift Out Time': formatShiftTime(rosterEntry?.out_time),
                              'Shortfall Hours': day.shortfall_hours || '00:00:00',
                              'Has Shortfall': day.has_shortfall ? 'Yes' : 'No',
                              'Grace In (Minutes)': rosterEntry?.grace_in || 'N/A',
                              'Grace Out (Minutes)': rosterEntry?.grace_out || 'N/A',
                              'Strategy Remarks': strategyRemarks,
                              'Shift Type': rosterEntry?.shift_name?.startsWith('OP') ? 'Open Shift' : 'Regular Shift',
                              'Week Off': isWeekOff ? 'Yes' : 'No'
                         });
                    });
               }
          });

          console.log(`📝 Exporting ${allEmployeesDailyData.length} daily records for ${employeeData.length} employees`);

          // Create the workbook
          const workbook = XLSX.utils.book_new();

          // Sheet 1: Summary
          const summaryWs = XLSX.utils.json_to_sheet(summaryData);
          XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');

          // Sheet 2: Employee Summary
          const detailedWs = XLSX.utils.json_to_sheet(detailedData);
          XLSX.utils.book_append_sheet(workbook, detailedWs, 'Employee Summary');

          // Sheet 3: Daily Attendance
          if (allEmployeesDailyData.length > 0) {
               const allDailyWs = XLSX.utils.json_to_sheet(allEmployeesDailyData);

               const range = XLSX.utils.encode_range({
                    s: { c: 0, r: 0 },
                    e: { c: 19, r: allEmployeesDailyData.length }
               });
               allDailyWs['!autofilter'] = { ref: range };

               allDailyWs['!cols'] = [
                    { width: 15 }, // Employee Code
                    { width: 25 }, // Employee Name  
                    { width: 12 }, // Date
                    { width: 10 }, // Day
                    { width: 10 }, // Punch In
                    { width: 10 }, // Punch Out
                    { width: 18 }, // Actual Worked Hours
                    { width: 20 }, // Considered Worked Hours
                    { width: 12 }, // Status
                    { width: 12 }, // Shift Name
                    { width: 12 }, // Shift Hours
                    { width: 12 }, // Shift In Time
                    { width: 12 }, // Shift Out Time
                    { width: 15 }, // Shortfall Hours
                    { width: 12 }, // Has Shortfall
                    { width: 15 }, // Grace In
                    { width: 15 }, // Grace Out
                    { width: 20 }, // Strategy Remarks
                    { width: 15 }, // Shift Type
                    { width: 12 }  // Week Off
               ];

               XLSX.utils.book_append_sheet(workbook, allDailyWs, 'All Employees Daily Attendance');
          }

          const filename = `Employee_Attendance_Summary_${selectedMonth}_to_${selectedToMonth}_${new Date().toISOString().split('T')[0]}.xlsx`;
          XLSX.writeFile(workbook, filename);

          console.log(`✅ Excel export completed: ${filename}`);
     };

     const getAttendanceColor = (percentage) => {
          if (percentage >= 90) return 'success';
          if (percentage >= 75) return 'warning';
          return 'danger';
     };

     return (
          <Container fluid className="doctor-attendance-summary py-2">
               {/* Summary Cards */}
               <Row className="mb-2">
                    <Col>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center row">
                                   <FaUsers size={20} className="text-primary col-auto" />
                                   <small className="text-muted col-auto">Total Employees</small>
                                   <h5 className="mb-0 col-auto">{summary.total_employees || 0}</h5>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center row">
                                   <FaChartBar size={20} className="text-success col-auto" />
                                   <small className="text-muted col-auto">With Attendance</small>
                                   <h5 className="mb-0 col-auto">{summary.employees_with_attendance || 0}</h5>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center row">
                                   <FaCalendarAlt size={20} className="text-info col-auto" />
                                   <small className="text-muted col-auto">Total Records</small>
                                   <h5 className="mb-0 col-auto">{summary.total_attendance_records || 0}</h5>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center justify-content-center">
                                   <button
                                        onClick={exportToExcel}
                                        disabled={loading || employeeData.length === 0}
                                        className="btn btn-sm btn-primary d-flex align-items-center px-2 py-1"
                                        style={{ fontSize: '0.75rem' }}
                                   >
                                        <FaDownload className="me-1" size={12} />
                                        <span>Export Excel</span>
                                   </button>
                              </Card.Body>
                         </Card>
                    </Col>
               </Row>

               {/* Filters */}
               <Row className="mb-2">
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">From Month</Form.Label>
                              <Form.Control
                                   type="month"
                                   value={selectedMonth}
                                   onChange={handleFromMonthChange}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">To Month</Form.Label>
                              <Form.Control
                                   type="month"
                                   value={selectedToMonth}
                                   onChange={(e) => setSelectedToMonth(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={5}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">Search Employee</Form.Label>
                              <Form.Control
                                   type="text"
                                   placeholder="Search by name or employee code"
                                   value={searchTerm}
                                   onChange={(e) => setSearchTerm(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">&nbsp;</Form.Label>
                              <Button
                                   variant="primary"
                                   onClick={fetchEmployeeAttendance}
                                   disabled={loading}
                                   className="px-2 py-1 d-flex align-items-center justify-content-center"
                                   size="sm"
                                   style={{ fontSize: '0.8rem', minWidth: 'auto' }}
                              >
                                   {loading ? <Spinner size="sm" /> : 'Refresh'}
                              </Button>
                         </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                         <Badge bg="info" className="w-100 text-center py-2">
                              {filteredEmployees.length} found
                         </Badge>
                    </Col>
               </Row>

               {/* Data Table */}
               <Card className="shadow-sm">
                    <Card.Body className="p-0">
                         {loading ? (
                              <div className="text-center py-3">
                                   <Spinner animation="border" size="sm" />
                                   <p className="mt-2 mb-0 small">Loading...</p>
                              </div>
                         ) : (
                              <>
                                   <div className="table-responsive">
                                        <Table striped hover className="mb-0 table-sm">
                                             <thead className="table-success">
                                                  <tr>
                                                       <th className="py-2">#</th>
                                                       <th className="py-2">Code</th>
                                                       <th className="py-2">Employee Name</th>
                                                       <th className="py-2">Shift Timing</th>
                                                       <th className="py-2">Present</th>
                                                       <th className="py-2">Week Off</th>
                                                       <th className="py-2">Absent</th>
                                                       <th className="py-2">Actual Hrs</th>
                                                       <th className="py-2">Consider Hrs</th>

                                                  </tr>
                                             </thead>
                                             <tbody>
                                                  {paginatedEmployees.map((employee, index) => {
                                                       const stats = calculateEmployeeStats(employee);
                                                       const globalIndex = (currentPage - 1) * recordsPerPage + index + 1;

                                                       return (
                                                            <tr key={employee.emp_id}>
                                                                 <td className="py-1">{globalIndex}</td>
                                                                 <td className="py-1">
                                                                      <code className="small">{employee.employee_code}</code>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <div>
                                                                           <strong className="small">{employee.employee_name}</strong>
                                                                      </div>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <span className="small text-muted">{stats.defaultShiftTiming}</span>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge bg="success" className="small">{stats.presentDays}</Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge bg="info" className="small">{stats.weekOffDays}</Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge bg="danger" className="small">{stats.absentDays}</Badge>
                                                                 </td>

                                                                 <td className="py-1">
                                                                      <span className="font-monospace text-info small">
                                                                           {stats.totalActualWorkingHours}h
                                                                      </span>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <span className="font-monospace text-success small">
                                                                           {stats.totalConsideredWorkingHours}h
                                                                      </span>
                                                                 </td>

                                                            </tr>
                                                       );
                                                  })}
                                             </tbody>
                                        </Table>
                                   </div>

                                   {/* Pagination */}
                                   {totalPages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center p-2 border-top">
                                             <span className="text-muted small">
                                                  {((currentPage - 1) * recordsPerPage) + 1}-{Math.min(currentPage * recordsPerPage, filteredEmployees.length)} of {filteredEmployees.length}
                                             </span>
                                             <div>
                                                  <Button
                                                       variant="outline-primary"
                                                       size="sm"
                                                       disabled={currentPage === 1}
                                                       onClick={() => setCurrentPage(currentPage - 1)}
                                                       className="btn btn-sm me-2"
                                                  >
                                                       ‹
                                                  </Button>
                                                  <span className="mx-2 small">
                                                       {currentPage}/{totalPages}
                                                  </span>
                                                  <Button
                                                       variant="outline-primary"
                                                       className='btn btn-sm'
                                                       size="sm"
                                                       disabled={currentPage === totalPages}
                                                       onClick={() => setCurrentPage(currentPage + 1)}
                                                  >
                                                       ›
                                                  </Button>
                                             </div>
                                        </div>
                                   )}
                              </>
                         )}
                    </Card.Body>
               </Card>
          </Container>
     );
};

export default EmployeeAttendanceSummary;